import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { environment } from 'environments/environment';
import { Subject, Subscription, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class InactivityService implements OnDestroy {
    private SESSION_TIMEOUT: number = 10 * 60 * 1000;
    private WARNING_TIME: number = 2 * 60 * 1000;
    private readonly COUNTDOWN_INTERVAL = 1000;

    private readonly STORAGE_KEY_LOGOUT_TIME = 'session_logout_time';
    private readonly STORAGE_KEY_WARNING_ACTIVE = 'session_warning_active';
    private readonly STORAGE_KEY_LAST_ACTIVITY = 'session_last_activity_time';

    private warningSubject = new Subject<number>();
    private logoutSubject = new Subject<void>();
    public warning$ = this.warningSubject.asObservable();
    public logout$ = this.logoutSubject.asObservable();

    private inactivityTimer: Subscription;
    private countdownTimer: Subscription;
    private destroy$ = new Subject<void>();
    private isWarningActive = false;
    private lastActivityTime: number;
    private activityListener: () => void;
    private logoutTime: number = 0;
    private broadcastChannel: BroadcastChannel;

    private readonly ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    constructor(private ngZone: NgZone) {
        this.lastActivityTime = Date.now();
        this.setupBroadcastChannel();
        this.autoRestoreTimer();
    }

    private autoRestoreTimer(): void {
        const storedLogoutTime = localStorage.getItem(this.STORAGE_KEY_LOGOUT_TIME);
        const storedWarningActive = localStorage.getItem(this.STORAGE_KEY_WARNING_ACTIVE);

        if (storedLogoutTime && storedWarningActive === 'true') {
            const timeUntilLogout = parseInt(storedLogoutTime, 10) - Date.now();
            if (timeUntilLogout > 0) {
                this.logoutTime = parseInt(storedLogoutTime, 10);
                this.isWarningActive = true;
                this.setupActivityListeners();
                this.startSyncedCountdown();
            }
        }
    }

    private setupBroadcastChannel(): void {
        try {
            this.broadcastChannel = new BroadcastChannel('user_activity_channel');
            this.broadcastChannel.onmessage = (event) => {
                switch (event.data.type) {
                    case 'activity':
                        this.onRemoteActivity(event.data);
                        break;
                    case 'logout':
                        this.forceLogout();
                        break;
                    case 'stayLoggedIn':
                        this.resetInactivity();
                        break;
                    case 'warningStarted':
                        this.syncWarningFromOtherTab(event.data);
                        break;
                    case 'requestWarningState':
                        this.broadcastWarningState();
                        break;
                }
            };
        } catch (e) {
            console.warn('BroadcastChannel not supported:', e);
        }
    }

    private broadcast(message: any): void {
        try {
            if (this.broadcastChannel) {
                this.broadcastChannel.postMessage(message);
            }
        } catch (e) {
            console.warn('Broadcast error:', e);
        }
    }

    private requestWarningState(): void {
        this.broadcast({ type: 'requestWarningState' });
    }

    private broadcastWarningState(): void {
        if (this.isWarningActive) {
            this.broadcast({
                type: 'warningState',
                logoutTime: this.logoutTime,
                isWarningActive: this.isWarningActive
            });
        }
    }

    private broadcastActivity(): void {
        this.broadcast({ type: 'activity', lastActivityTime: this.lastActivityTime });
    }

    private onRemoteActivity(data: any): void {
        if (!this.isWarningActive) {
            this.lastActivityTime = data.lastActivityTime || Date.now();
            localStorage.setItem(this.STORAGE_KEY_LAST_ACTIVITY, this.lastActivityTime.toString());
            this.clearTimers();
            this.startInactivityTimer();
        }
    }

    private broadcastWarningStarted(): void {
        this.broadcast({ type: 'warningStarted', logoutTime: this.logoutTime });
    }

    private syncWarningFromOtherTab(data: any): void {
        if (!this.isWarningActive) {
            this.logoutTime = data.logoutTime;
            this.isWarningActive = true;
            localStorage.setItem(this.STORAGE_KEY_LOGOUT_TIME, this.logoutTime.toString());
            localStorage.setItem(this.STORAGE_KEY_WARNING_ACTIVE, 'true');
            this.startSyncedCountdown();
        }
    }

    public broadcastLogout(): void {
        this.broadcast({ type: 'logout' });
    }

    public broadcastStayLoggedIn(): void {
        this.broadcast({ type: 'stayLoggedIn' });
    }

    public startTracking(): void {
        if (this.isWarningActive) return;

        const storedLogoutTime = localStorage.getItem(this.STORAGE_KEY_LOGOUT_TIME);
        const storedWarningActive = localStorage.getItem(this.STORAGE_KEY_WARNING_ACTIVE);
        const storedLastActivity = localStorage.getItem(this.STORAGE_KEY_LAST_ACTIVITY);

        if (storedLastActivity) {
            this.lastActivityTime = parseInt(localStorage.getItem(this.STORAGE_KEY_LAST_ACTIVITY), 10);
        }

        if (storedLogoutTime && storedWarningActive === 'true') {
            this.logoutTime = parseInt(storedLogoutTime, 10);
            if (this.logoutTime - Date.now() > 0) {
                this.isWarningActive = true;
                this.setupActivityListeners();
                this.startSyncedCountdown();
                return;
            } else {
                localStorage.removeItem(this.STORAGE_KEY_LOGOUT_TIME);
                localStorage.removeItem(this.STORAGE_KEY_WARNING_ACTIVE);
                localStorage.removeItem(this.STORAGE_KEY_LAST_ACTIVITY);
            }
        }

        this.setupActivityListeners();
        this.requestWarningState();
        setTimeout(() => {
            if (!this.isWarningActive) {
                this.startInactivityTimer();
            }
        }, 300);
    }

    public stopTracking(): void {
        this.removeActivityListeners();
        this.clearTimers();
    }

    public resetInactivity(): void {
        localStorage.removeItem(this.STORAGE_KEY_LOGOUT_TIME);
        localStorage.removeItem(this.STORAGE_KEY_WARNING_ACTIVE);
        localStorage.removeItem(this.STORAGE_KEY_LAST_ACTIVITY);

        this.lastActivityTime = Date.now();
        this.isWarningActive = false;
        this.logoutTime = 0;
        this.clearTimers();
        this.warningSubject.next(0);
        this.setupActivityListeners();
        this.startInactivityTimer();
    }

    public getRemainingTime(): number {
        const remaining = this.SESSION_TIMEOUT - (Date.now() - this.lastActivityTime);
        return remaining > 0 ? remaining : 0;
    }

    public forceLogout(): void {
        this.logoutSubject.next();
        this.stopTracking();
    }

    private setupActivityListeners(): void {
        this.ngZone.runOutsideAngular(() => {
            this.activityListener = () => this.onUserActivity();
            this.ACTIVITY_EVENTS.forEach(event => {
                document.addEventListener(event, this.activityListener, false);
            });
        });
    }

    private removeActivityListeners(): void {
        if (this.activityListener) {
            this.ACTIVITY_EVENTS.forEach(event => {
                document.removeEventListener(event, this.activityListener, false);
            });
        }
    }

    private onUserActivity(): void {
        if (this.isWarningActive) return;

        if (Date.now() - this.lastActivityTime > 500) {
            this.lastActivityTime = Date.now();
            localStorage.setItem(this.STORAGE_KEY_LAST_ACTIVITY, this.lastActivityTime.toString());
            this.broadcastActivity();
            this.clearTimers();
            this.startInactivityTimer();
        }
    }

    private startInactivityTimer(): void {
        this.clearTimers();
        const timeUntilWarning = this.SESSION_TIMEOUT - this.WARNING_TIME;
        this.inactivityTimer = timer(timeUntilWarning).subscribe(() => this.showWarning());
    }

    public updateSessionTimeout(timeoutMs: number): void {
        this.SESSION_TIMEOUT = timeoutMs;
        this.WARNING_TIME = Math.min(30000, timeoutMs * 0.1);
        this.clearTimers();
        this.startInactivityTimer();
    }

    private showWarning(): void {
        this.ngZone.run(() => {
            this.isWarningActive = true;
            this.logoutTime = Date.now() + this.WARNING_TIME;
            localStorage.setItem(this.STORAGE_KEY_LOGOUT_TIME, this.logoutTime.toString());
            localStorage.setItem(this.STORAGE_KEY_WARNING_ACTIVE, 'true');
            this.broadcastWarningStarted();
            this.startSyncedCountdown();
        });
    }

    private startSyncedCountdown(): void {
        this.clearTimers();
        this.countdownTimer = timer(0, this.COUNTDOWN_INTERVAL)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                const secondsUntilLogout = (this.logoutTime - Date.now()) / 1000;
                if (secondsUntilLogout > 0) {
                    this.ngZone.run(() => {
                        this.warningSubject.next(Math.ceil(secondsUntilLogout));
                    });
                } else {
                    this.clearTimers();
                    this.ngZone.run(() => {
                        this.logoutSubject.next();
                        this.stopTracking();
                    });
                }
            });
    }

    private clearTimers(): void {
        this.inactivityTimer?.unsubscribe();
        this.countdownTimer?.unsubscribe();
    }

    ngOnDestroy(): void {
        this.stopTracking();
        this.destroy$.next();
        this.destroy$.complete();
        this.warningSubject.complete();
        this.logoutSubject.complete();

        try {
            if (this.broadcastChannel) {
                this.broadcastChannel.close();
            }
        } catch (e) {
            console.warn('Error closing broadcast channel:', e);
        }
    }
}